import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Calendar } from 'lucide-react';
import { fetchEvents } from '@/services/api';
import { useToast } from '@/hooks/use-toast';
import { format, isAfter, isBefore, isSameDay, parseISO } from 'date-fns';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Helmet } from 'react-helmet-async';
import SeoHead from '@/components/Seo';

interface Ministry {
  id: number;
  name: string;
}

interface Event {
  id: string;
  title: string;
  description: string;
  start_date: string;
  end_date: string;
  location: string;
  category?: string;
  photo: string;
  slug: string;
  ministry?: Ministry;
  ministry_id: string;
}

const Events = () => {
  const [events, setEvents] = useState<Event[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedMinistry, setSelectedMinistry] = useState<string>('All');
  const { toast } = useToast();

  useEffect(() => {
    const loadEvents = async () => {
      try {
        const response = await fetchEvents();
        setEvents(response.data.events);
      } catch (error) {
        console.error('Error fetching events:', error);
        toast({
          title: "Note",
          description: "Using sample data. API connection will be established for live data.",
          variant: "default"
        });
        setEvents([]); // fallback empty
      } finally {
        setLoading(false);
      }
    };
    loadEvents();
  }, [toast]);

  const ministries = ['All', ...Array.from(new Set(events.map(e => e.ministry?.name).filter(Boolean)))];

  const filteredEvents = selectedMinistry === 'All'
    ? events
    : events.filter(e => e.ministry?.name === selectedMinistry);

  const groupByMonth = (events: Event[]) => {
    const grouped: Record<string, Event[]> = {};
    events.forEach((event) => {
      const date = parseISO(event.start_date);
      const key = format(date, 'MMMM yyyy');
      if (!grouped[key]) grouped[key] = [];
      grouped[key].push(event);
    });
    return grouped;
  };

  const sortEvents = (events: Event[]) => {
    const now = new Date();
    const upcoming: Event[] = [];
    const past: Event[] = [];
    const ongoing: Event[] = [];

    events.forEach(event => {
      const start = parseISO(event.start_date);
      const end = parseISO(event.end_date);

      if (isBefore(end, now)) {
        past.push(event);
      } else if (isAfter(start, now)) {
        upcoming.push(event);
      } else {
        ongoing.push(event);
      }
    });

    return { upcoming, ongoing, past };
  };

  const getCategoryColor = (category: string) => {
    const colors: Record<string, string> = {
      '1': 'bg-purple-100 text-purple-800',
      '2': 'bg-blue-100 text-blue-800',
      '4': 'bg-green-100 text-green-800',
      '3': 'bg-yellow-100 text-yellow-800',
      '5': 'bg-red-100 text-red-800',
    };
    return colors[category] || 'bg-gray-100 text-gray-800';
  };

  const formatDateRange = (start: string, end: string) => {
    const startDate = parseISO(start);
    const endDate = parseISO(end);
    const sameDay = isSameDay(startDate, endDate);
    return sameDay
      ? format(startDate, 'PPP')
      : `${format(startDate, 'PPP')} – ${format(endDate, 'PPP')}`;
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gray-50 py-16 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-purple-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Loading events...</p>
        </div>
      </div>
    );
  }

  const { upcoming, ongoing, past } = sortEvents(filteredEvents);
  const groupedEvents = groupByMonth([...upcoming, ...ongoing]);

  return (
    <>
      <SeoHead
        title="Upcoming & Ongoing Events"
        description="Stay informed with the latest upcoming and ongoing events in the Diocese of Ibadan South — a Bible-based, God-centered, and Spirit-guided community."
      // url is optional — it will auto-detect the current page URL
      />
      <div className="min-h-screen bg-gray-50 py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-12">
            <h1 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              Upcoming & Ongoing Events
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Stay connected with diocesan activities, worship services, and special programs
            </p>
          </div>

          {/* Ministry Filter Tabs */}
          <div className="flex flex-wrap justify-center gap-4 mb-12">
            {ministries.map((ministry) => (
              <button
                key={ministry}
                onClick={() => setSelectedMinistry(ministry)}
                className={`px-4 py-2 rounded-full text-sm font-medium transition-all border ${selectedMinistry === ministry
                  ? 'bg-blue-600 text-white border-blue-600'
                  : 'bg-white text-blue-600 border-blue-200 hover:bg-blue-50'
                  }`}
              >
                {ministry}
              </button>
            ))}
          </div>

          {/* Grouped Events */}
          {Object.entries(groupedEvents).map(([month, events]) => (
            <div key={month} className="mb-10">
              <h2 className="text-2xl font-bold text-gray-800 mb-6">{month}</h2>
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                {events.map((event) => (
                  <Link key={event.id} to={`/events/${event.slug}`}>
                    <motion.div
                      whileHover={{ scale: 1.03 }}
                      whileTap={{ scale: 0.97 }}
                      transition={{ duration: 0.2 }}
                    >
                      <Card className="border-none shadow-lg hover:shadow-xl transition-shadow cursor-pointer rounded-xl overflow-hidden">
                        {event.photo && (
                          <img
                            src={event.photo?.startsWith('http') ? event.photo : `https://api.ibadansouthanglicandiocese.org.ng/${event.photo}`}
                            alt={event.title}
                            className="w-full h-60 object-cover"
                          />
                        )}
                        <CardHeader>
                          <div className="flex items-start justify-between">
                            <div className="flex-1">
                              <CardTitle className="text-xl text-gray-900 mb-2">{event.title}</CardTitle>
                              {event.ministry && (
                                <Badge className={getCategoryColor(event.ministry_id || 'General')}>
                                  {event.ministry.name || 'General'}
                                </Badge>
                              )}
                            </div>
                            <div className="ml-4 text-right">
                              <div className="flex items-center text-purple-600 mb-1">
                                <Calendar className="h-4 w-4 mr-1" />
                                <span className="text-sm font-medium">
                                  {format(parseISO(event.start_date), 'p')}
                                </span>
                              </div>
                            </div>
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className="space-y-3">
                            <div>
                              <p className="text-lg font-medium text-gray-900 mb-1">
                                {formatDateRange(event.start_date, event.end_date)}
                              </p>
                              <p className="text-gray-600 line-clamp-4">{event.description}</p>
                            </div>
                            <div className="text-sm text-gray-500">
                              <span className="font-medium">Location: </span>
                              {event.location}
                            </div>
                            {event.ministry?.name && (
                              <div className="text-sm text-gray-700">
                                <span className="font-semibold">Ministry: </span>
                                {event.ministry.name}
                              </div>
                            )}
                          </div>
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Link>
                ))}
              </div>
            </div>
          ))}

          {/* Past Events */}
          <div className="mt-20">
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Past Events</h2>
            {past.length === 0 ? (
              <p className="text-gray-600">No past events yet.</p>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                {past.map(event => (
                  <Link key={event.id} to={`/events/${event.slug}`}>
                    <motion.div whileHover={{ scale: 1.02 }} transition={{ duration: 0.2 }}>
                      <Card className="border shadow-sm hover:shadow-md transition-shadow rounded-xl">
                        {event.photo && (
                          <img
                            src={event.photo?.startsWith('http') ? event.photo : `https://api.ibadansouthanglicandiocese.org.ng/${event.photo}`}
                            alt={event.title}
                            className="w-full h-60 object-cover"
                          />
                        )}
                        <CardHeader>
                          <CardTitle className="text-lg text-gray-800">{event.title}</CardTitle>
                        </CardHeader>
                        <CardContent>
                          <p className="text-sm text-gray-600">{formatDateRange(event.start_date, event.end_date)}</p>
                          <p className="text-sm text-gray-500">{event.location}</p>
                          {event.ministry?.name && (
                            <p className="text-sm text-gray-700 mt-1">
                              <strong>Ministry:</strong> {event.ministry.name}
                            </p>
                          )}
                        </CardContent>
                      </Card>
                    </motion.div>
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
};

export default Events;
